import React from 'react';

import PropTypes from 'prop-types';

const AddIcon = React.lazy(() => import('./components/AddIcon'));
const AlertIcon = React.lazy(() => import('./components/AlertIcon'));
const AmexCardExampleIcon = React.lazy(() => import('./components/AmexCardExampleIcon'));
const AmexIcon = React.lazy(() => import('./components/AmexIcon'));
const ApplePayIcon = React.lazy(() => import('./components/ApplePayIcon'));
const ArrowDownIcon = React.lazy(() => import('./components/ArrowDownIcon'));
const ArrowRightIcon = React.lazy(() => import('@/lib/components/Icon/components/ArrowRightIcon/ArrowRightIcon'));
const BancontactIcon = React.lazy(() => import('./components/BancontactIcon'));
const BankIcon = React.lazy(() => import('@/lib/components/Icon/components/BankIcon/BankIcon'));
const CardExampleIcon = React.lazy(() => import('@/lib/components/Icon/components/CardExampleIcon/CardExampleIcon'));
const CbIcon = React.lazy(() => import('./components/CbIcon'));
const CheckedIcon = React.lazy(() => import('@/lib/components/Icon/components/CheckedIcon/CheckedIcon'));
const CrossIcon = React.lazy(() => import('./components/CrossIcon'));
const DinerClubInternationalIcon = React.lazy(() => import('./components/DinerClubInternationalIcon'));
const DiscoverIcon = React.lazy(() => import('./components/DiscoverIcon'));
const GooglePayIcon = React.lazy(() => import('./components/GooglePayIcon'));
const InfoIcon = React.lazy(() => import('./components/InfoIcon'));
const KlarnaIcon = React.lazy(() => import('./components/KlarnaIcon'));
const MaestroIcon = React.lazy(() => import('./components/MaestroIcon'));
const MastercardIcon = React.lazy(() => import('./components/MastercardIcon'));
const MobilePayIcon = React.lazy(() => import('./components/MobilePayIcon'));
const PayPalIcon = React.lazy(() => import('./components/PayPalIcon'));
const PostFinanceIcon = React.lazy(() => import('./components/PostFinanceIcon'));
const TwintIcon = React.lazy(() => import('./components/TwintIcon'));
const TooltipIcon = React.lazy(() => import('./components/TooltipIcon'));
const VisaIcon = React.lazy(() => import('./components/VisaIcon'));
const RevolutIcon = React.lazy(() => import('./components/RevolutIcon'));
const WeroIcon = React.lazy(() => import('./components/WeroIcon'));

const MAPPING_ID_ICON = {
    add: AddIcon,
    alert: AlertIcon,
    Amex: AmexIcon,
    AMEX: AmexIcon,
    amexCardExampleCvv: AmexCardExampleIcon,
    ApplePay: ApplePayIcon,
    arrowDown: ArrowDownIcon,
    arrowRight: ArrowRightIcon,
    Bancontact: BancontactIcon,
    BonoCulturalJoven: MastercardIcon,
    cardExampleCvv: CardExampleIcon,
    CB: CbIcon,
    checked: CheckedIcon,
    ChileanForm: BankIcon,
    Clabe: BankIcon,
    CLABE: BankIcon,
    ColombianForm: BankIcon,
    cross: CrossIcon,
    DinersClub: DinerClubInternationalIcon,
    Discover: DiscoverIcon,
    BankTransfer: BankIcon,
    GooglePay: GooglePayIcon,
    Iban: BankIcon,
    IBAN: BankIcon,
    info: InfoIcon,
    InternationalBank: BankIcon,
    InternationalTransfersMexico: BankIcon,
    Klarna: KlarnaIcon,
    KlarnaPayLater: KlarnaIcon,
    LinkRevolut: RevolutIcon,
    Maestro: MaestroIcon,
    MAESTRO: MaestroIcon,
    MasterCard: MastercardIcon,
    MASTERCARD: MastercardIcon,
    MobilePay: MobilePayIcon,
    Paypal: PayPalIcon,
    paypal: PayPalIcon,
    PeruvianForm: BankIcon,
    PostFinanceCard: PostFinanceIcon,
    PostFinanceEfinance: PostFinanceIcon,
    RevolutPay: RevolutIcon,
    Sofort: KlarnaIcon,
    SouthAfricanForm: BankIcon,
    HongKongForm: BankIcon,
    TWINT: TwintIcon,
    tooltip: TooltipIcon,
    Visa: VisaIcon,
    VISA: VisaIcon,
    Wero: WeroIcon,
    WeezPayOverTime: () => <></>,
};

/**
 * This component provides a way to render an icon (from the available list) asynchronously (lazy).
 */
const Icon = ({
    id,
    themeMode = 'light',
    fallback = null,
    style = null,
    className = null,
}) => {
    const fallbackValue = fallback || <span />;

    if (!id) {
        return null;
    }

    return (
        <React.Suspense fallback={fallbackValue}>
            {Object.keys(MAPPING_ID_ICON).includes(id)
                ? React.createElement(MAPPING_ID_ICON[id], { style, className, themeMode })
                : fallback}
        </React.Suspense>
    );
};

Icon.propTypes = {
    /**
     * The id of the icon to display, must exist in the list of the available icons.
     */
    id: PropTypes.oneOf([
        'add',
        'alert',
        'Amex',
        'AMEX',
        'amexCardExampleCvv',
        'ApplePay',
        'arrowDown',
        'arrowRight',
        'Bancontact',
        'cardExampleCvv',
        'CB',
        'checked',
        'ChileanForm',
        'Clabe',
        'CLABE',
        'ColombianForm',
        'cross',
        'DinersClub',
        'Discover',
        'GooglePay',
        'Iban',
        'IBAN',
        'IDeal',
        'info',
        'InternationalBank',
        'Klarna',
        'KlarnaPayLater',
        'Maestro',
        'MAESTRO',
        'MasterCard',
        'MASTERCARD',
        'MobilePay',
        'Paypal',
        'paypal',
        'PeruvianForm',
        'tooltip',
        'PostFinanceCard',
        'PostFinanceEfinance',
        'RevolutPay',
        'Sofort',
        'SouthAfricanForm',
        'TWINT',
        'Visa',
        'VISA',
        'HongKongForm',
        'WeezPayOverTime',
        'Wero',
    ]),
    /**
     * The theme mode to display the appropriate version of the icon (if exist).
     */
    themeMode: PropTypes.oneOf(['light', 'dark']),
    /**
     * The fallback, the component or the text to display when the icon is not yet loaded or failed to be loaded.
     */
    fallback: PropTypes.any,
    /**
     * The style to add to the icon wrapper.
     */
    style: PropTypes.object,
    /**
     * The class names to add to the icon wrapper.
     */
    className: PropTypes.string,
};

export default Icon;
